import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faSitemap } from '@fortawesome/free-solid-svg-icons/faSitemap';
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons/faEnvelopeOpenText';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons/faBriefcase';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons/faCheckSquare';
import { Store } from '@ngrx/store';
import { Subscription, take, mergeMap, of, throwError, BehaviorSubject, filter, Observable, skip, forkJoin, observeOn, catchError } from 'rxjs';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { RoleInterface } from '../../../models/interface/role.interface';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { HelperService } from '../../../services/helper/helper.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { GetAllUsersAdminPayload, UserActionTypes } from '../../../store/user/user.action';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { OrganisationInterface } from '../../../models/interface/organisation/organisation.interface';
import { UserStore } from 'src/app/store/user/component-store/user.store';

@Component({
  selector: 'app-card-study',
  templateUrl: './card-study.component.html',
  styleUrls: ['./card-study.component.scss'],
  providers: [UserStore],
})
export class CardStudyComponent implements OnChanges, OnDestroy, OnInit {
  @Output()
  public emitUpdatePerformed = new EventEmitter<string>();

  @Output()
  public emitDelete = new EventEmitter<void>();

  @Output()
  public emitUpdate = new EventEmitter<void>();

  // Icons
  faSearch = faSearch;
  faList = faList;
  faUsers = faUsers;
  faKey = faKey;
  faEnvelopeOpenText = faEnvelopeOpenText;
  faSitemap = faSitemap;
  faBriefcase = faBriefcase;
  faCheckSquare = faCheckSquare;
  faStar = faStar;
  faPlus = faPlus;
  faPen = faPen;
  faTrash = faTrash;

  public study: StudyInterface;

  public profile$: Observable<ProfileInterface>;
  public profileRoles$: Observable<RoleInterface[]>;

  public isOrganisationCollaborator$: Observable<boolean>;

  public isAdmin$: Observable<boolean>;

  public showAdmin = false;

  public patient: UserInterface;

  public organisations: OrganisationInterface[] = [];
  public organisation: OrganisationInterface | undefined = undefined;

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;
  private allCollaborators$: Observable<UserInterface[]>;

  private users$: Observable<UserInterface[]>;

  private groups$: Observable<StudyInterface[]>;

  private groupsSubject = new BehaviorSubject<StudyInterface[]>([]);

  private isOrganisationCollaboratorSubject = new BehaviorSubject<boolean>(false);

  private isAdminSubject = new BehaviorSubject<boolean>(false);

  private collaboratingIds: number[];

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private userStore: UserStore,
    private store: Store<{
      myProfile: ProfileInterface;
      myRoles: RoleInterface[];
      users: UserInterface[];
      collabGroups: StudyInterface[];
    }>,
  ) {
    this.profile$ = store.select('myProfile');
    this.profileRoles$ = store.select('myRoles');
    this.users$ = this.store.select('users');
    this.groups$ = this.store.select('collabGroups');
    this.isOrganisationCollaborator$ = this.isOrganisationCollaboratorSubject.asObservable();
    this.allCollaborators$ = this.userStore.allCollaborators$;
    this.isAdmin$ = this.isAdminSubject.asObservable();
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  @Input()
  _study: StudyInterface;

  @Input()
  _patient: UserInterface;

  @Input()
  _isAdmin: boolean;

  @Input()
  _organisations: OrganisationInterface[];

  ngOnChanges(changes: SimpleChanges) {
    const { _study, _patient, _isAdmin, _organisations } = changes;
    if (_study?.currentValue) {
      this.study = _study.currentValue;
    }
    if (_organisations?.currentValue) {
      this.organisations = _organisations.currentValue;
    }
    // Whenever _study or _organisations change, update organisation and isOrganisationManager
    if ((_study?.currentValue || _organisations?.currentValue) && this.organisations) {
      this.organisation = this.organisations.find((org) => org.id == this.study.attributes.organisation_id);
      const isOrganisationManager =
        this.helper.hasOrganisationManagerRoles(this.organisation?.relationships?.roles?.data);
      this.isOrganisationCollaboratorSubject.next(isOrganisationManager);
    }
    if (_patient?.currentValue) {
      this.patient = _patient.currentValue;
    }
    if (_isAdmin?.currentValue !== undefined) {
      this.showAdmin = _isAdmin.currentValue;
      this.isAdminSubject.next(this.showAdmin);
    }
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.groups$.subscribe({
        next: (result) => {
          const groups = result.filter(
            (group) => group.attributes.type === 'study' || group.attributes.type === 'organisation',
          );
          this.groupsSubject.next(groups);
          this.collaboratingIds = groups.map((obj) => obj.id);
          const foundStudy = groups.find((group) => group.id.toString() === this.study.id.toString());
          if (foundStudy) {
            this.study = foundStudy;
          }
        },
        error: (error) => {
          console.error(error);
          this.groupsSubject.next([]);
          this.collaboratingIds = [];
        },
      }),
    );
  }

  public isCollabId(id: number): boolean {
    return this.collaboratingIds !== undefined ? this.collaboratingIds.includes(id) : false;
  }

  public getDetailedStudy(id: number): void {
    this.router.navigateByUrl(`/groups/${id}`);
  }

  public getPatientOverview(): void {
    this.router.navigate(['/patients']);
  }

  isCollboratorOfOrganisation(): boolean {
    return this.organisation?.relationships?.roles?.data?.length > 0;
  }

  public getDetailedOrganisation(): void {
    this.router.navigateByUrl(`organisations/${this.organisation.id}/studies`);
  }

  public openDialogGroupCollaboratorOrganisationManager(study: StudyInterface): void {
    this.subscriptions.push(
      this.profileRoles$
        .pipe(
          filter((user) => user !== null),
          take(1),
          mergeMap((roles: RoleInterface[]) => {
            this.isAdminSubject.next(this.helperService.hasRoles(roles, /admin$/));

            this.store.dispatch({
              type: StudyActionTypes.getCollaboratorsType,
              payload: { studyId: study.id, include: 'roles' },
            });
            this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(study.id));
            const studyCollaborators = this.collaboratorsOfStudy$.pipe(skip(1), take(1));

            let eCoachesOrCollaborators: Observable<UserInterface[]>;
            if (this.isAdminSubject.value) {
              const payload: GetAllUsersAdminPayload = {
                roles: 'ecoach',
                isVerified: 1,
              };
              this.store.dispatch({ type: UserActionTypes.getAllUsersAdminType, payload });
              eCoachesOrCollaborators = this.users$.pipe(skip(1), take(1));
            } else {
              this.userStore.getAllCollaboratorsOfUser({ roles: 'ecoach' });
              eCoachesOrCollaborators = this.allCollaborators$.pipe(
                skip(1),
                take(1),
                catchError(() => of([] as UserInterface[])),
              );
            }
            return forkJoin({ studyCollaborators, eCoachesOrCollaborators });
          }),
          mergeMap((results) => {
            const collaborators: UserInterface[] = results.studyCollaborators.collaborators;
            const isAdmin = this.isAdminSubject.value;
            let eCoaches: UserInterface[], organisationCollaborators: UserInterface[];
            if (isAdmin) {
              const result: UserInterface[] = results.eCoachesOrCollaborators;
              eCoaches = this.helper.getVerifiedEditorsNotInStudy(result, collaborators);
              organisationCollaborators = undefined;
            } else {
              const result = results.eCoachesOrCollaborators;
              eCoaches = [];
              organisationCollaborators = this.helper.getVerifiedEditorsNotInStudy(result, collaborators);
            }
            return this.helperDialog
              .openDialogGroupCollaboratorOrganisationManager(
                study,
                collaborators,
                eCoaches,
                organisationCollaborators,
                isAdmin,
              )
              .afterClosed();
          })
        )
        .subscribe({
          next: (result: string) => {
            this.emitUpdatePerformed.emit(result);
          },
          error: (error) => {
            throwError(() => new Error('Loading error')).pipe(observeOn(error));
          },
        }),
    );
  }

  public openDialogAssignStudy(study: StudyInterface): void {
    this.helperDialog
      .openDialogAssignStudy(study)
      .afterClosed()
      .subscribe((result: string) => {
        this.emitUpdatePerformed.emit(result);
      });
  }

  public openDialogStudyUpdateAdmin(study: StudyInterface): void {
    this.helperDialog
      .openDialogStudyUpdate(study)
      .afterClosed()
      .subscribe(() => {
        this.emitUpdate.emit();
      });
  }

  public openDialogStudyDeleteAdmin(study: StudyInterface): void {
    this.helperDialog
      .openDialogStudyDelete(study)
      .afterClosed()
      .subscribe(() => {
        this.emitDelete.emit();
      });
  }

  public getStudyCodeFromStudy(): string {
    const patientCodes = this.patient.attributes.study_code;
    const found = patientCodes.find(
      (value) => value.study_id.toString() === this.study.id.toString(),
    );
    if (found) {
      return found.code;
    } else {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
